/**
 * MapLibre map plus the WebGL flow overlay.
 *
 * The overlay canvas sits above the map canvas and is kept in register by
 * recomputing a homography from the four projected corners of the domain on
 * every map move. Vector output — rotor hatching, the lift band — goes through
 * MapLibre's own GeoJSON layers so it stays crisp at any zoom and can be
 * queried on click.
 */

import { useCallback, useEffect, useRef } from 'react';
import maplibregl, { type LngLatLike, type Map as MlMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { MetricDomain } from '../dem/projection';
import { gridToLonLat } from '../dem/projection';
import { FlowRenderer, crossTimeForWind } from '../render/flowRenderer';
import { unitSquareToQuad } from '../render/homography';
import { currentWind, useStore } from '../state/store';
import type { ContourSet } from '../worker/api';
import type { FieldSlice } from '../physics/types';
import { metDegreesToVector } from '../physics/terrain';
import { HAZARD_COLOURS, rgbToCss } from '../render/colormaps';

const BASEMAP_ATTRIB =
  '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, © <a href="https://carto.com/attributions">CARTO</a>';

function baseStyle(): maplibregl.StyleSpecification {
  return {
    version: 8,
    glyphs: 'https://basemaps.cartocdn.com/gl/positron-gl-style/glyphs/{fontstack}/{range}.pbf',
    sources: {
      basemap: {
        type: 'raster',
        tiles: [
          'https://a.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png',
          'https://b.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png',
          'https://c.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png',
        ],
        tileSize: 256,
        maxzoom: 19,
        attribution: BASEMAP_ATTRIB,
      },
      // MapLibre decodes Terrarium natively, so hillshade costs nothing extra
      // beyond the tiles the DEM resolver is already warming.
      terrainDem: {
        type: 'raster-dem',
        tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
        encoding: 'terrarium',
        tileSize: 256,
        maxzoom: 15,
        attribution: 'Elevation: AWS Terrain Tiles',
      },
    },
    layers: [
      { id: 'bg', type: 'background', paint: { 'background-color': '#e8e6e1' } },
      { id: 'basemap', type: 'raster', source: 'basemap', paint: { 'raster-opacity': 0.85 } },
      {
        id: 'hillshade',
        type: 'hillshade',
        source: 'terrainDem',
        paint: {
          'hillshade-exaggeration': 0.45,
          'hillshade-shadow-color': '#2b2f36',
          'hillshade-highlight-color': '#ffffff',
        },
      },
    ],
  };
}

/** Diagonal hatch for rotor zones — hatching keeps the terrain legible under it. */
function hatchImage(colour: string): { data: Uint8Array; width: number; height: number } {
  const size = 12;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d')!;
  ctx.clearRect(0, 0, size, size);
  ctx.strokeStyle = colour;
  ctx.lineWidth = 2.2;
  ctx.beginPath();
  for (let i = -size; i < size * 2; i += 6) {
    ctx.moveTo(i, 0);
    ctx.lineTo(i + size, size);
  }
  ctx.stroke();
  const img = ctx.getImageData(0, 0, size, size);
  return { data: new Uint8Array(img.data.buffer), width: size, height: size };
}

export interface TerrainLayerData {
  width: number;
  height: number;
  elevation: Float32Array;
  fallX: Float32Array;
  fallY: Float32Array;
  /** null until the lazily-computed glide field has arrived */
  requiredGlide: Float32Array | null;
}

interface Props {
  domain: MetricDomain | null;
  slice: FieldSlice | null;
  contours: ContourSet | null;
  terrainLayers: TerrainLayerData | null;
  onMapReady: (map: MlMap) => void;
}

export function MapView({ domain, slice, contours, terrainLayers, onMapReady }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mapRef = useRef<MlMap | null>(null);
  const rendererRef = useRef<FlowRenderer | null>(null);
  const rafRef = useRef<number>(0);
  const lastFrame = useRef<number>(performance.now());
  const domainRef = useRef<MetricDomain | null>(null);
  const vectorDataRef = useRef<Record<string, GeoJSON.FeatureCollection> | null>(null);

  const layers = useStore((s) => s.layers);
  const windSpeedMs = useStore((s) => currentWind(s).speedMs);
  const windDirectionDeg = useStore((s) => currentWind(s).directionDeg);
  const crossSectionMode = useStore((s) => s.crossSectionMode);
  const setState = useStore((s) => s.set);

  domainRef.current = domain;

  // --- map lifecycle -------------------------------------------------------
  useEffect(() => {
    if (!containerRef.current) return;
    const store = useStore.getState();
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: baseStyle(),
      center: [store.lon, store.lat],
      zoom: store.zoom,
      maxPitch: 70,
      attributionControl: false,
    });
    mapRef.current = map;
    if (import.meta.env.DEV) {
      // Handle for inspecting sources and layers from the console during
      // development; never exposed in a production build.
      (window as unknown as { __map?: MlMap }).__map = map;
    }

    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'top-right');
    map.addControl(new maplibregl.ScaleControl({ maxWidth: 120, unit: 'metric' }), 'bottom-right');
    map.addControl(
      new maplibregl.AttributionControl({ compact: true }),
      'bottom-right'
    );

    map.on('load', () => {
      const rotor = rgbToCss(HAZARD_COLOURS[3]);
      const hatch = hatchImage(rotor);
      if (!map.hasImage('rotor-hatch')) map.addImage('rotor-hatch', hatch);

      map.addSource('separation', { type: 'geojson', data: emptyFc() });
      map.addSource('lift', { type: 'geojson', data: emptyFc() });
      map.addSource('domain', { type: 'geojson', data: emptyFc() });
      map.addSource('section', { type: 'geojson', data: emptyFc() });

      map.addLayer({
        id: 'separation-fill',
        type: 'fill',
        source: 'separation',
        paint: { 'fill-pattern': 'rotor-hatch', 'fill-opacity': 0.85 },
      });
      map.addLayer({
        id: 'separation-outline',
        type: 'line',
        source: 'separation',
        paint: { 'line-color': rotor, 'line-width': 1.4, 'line-opacity': 0.9 },
      });
      map.addLayer({
        id: 'lift-line',
        type: 'line',
        source: 'lift',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#ffffff', 'line-width': 2.6, 'line-opacity': 0.95 },
      });
      map.addLayer({
        id: 'lift-line-core',
        type: 'line',
        source: 'lift',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#1f9d55', 'line-width': 1.4 },
      });
      map.addLayer({
        id: 'domain-outline',
        type: 'line',
        source: 'domain',
        paint: {
          'line-color': '#3d7dff',
          'line-width': 1.2,
          'line-dasharray': [3, 3],
          'line-opacity': 0.65,
        },
      });
      map.addLayer({
        id: 'section-line',
        type: 'line',
        source: 'section',
        paint: { 'line-color': '#e4572e', 'line-width': 3, 'line-opacity': 0.95 },
      });

      onMapReady(map);
    });

    const syncCentre = () => {
      const c = map.getCenter();
      setState({ lat: c.lat, lon: c.lng, zoom: map.getZoom() });
    };
    map.on('moveend', syncCentre);

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // Intentionally mounted once; state is read imperatively inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- renderer lifecycle --------------------------------------------------
  useEffect(() => {
    if (!canvasRef.current) return;
    let renderer: FlowRenderer;
    try {
      renderer = new FlowRenderer(canvasRef.current);
    } catch (err) {
      console.error('Flow overlay unavailable:', err);
      return;
    }
    rendererRef.current = renderer;

    const frame = (now: number) => {
      const dt = (now - lastFrame.current) / 1000;
      lastFrame.current = now;
      const map = mapRef.current;
      const dom = domainRef.current;
      if (map && dom) {
        const rect = map.getContainer().getBoundingClientRect();
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        renderer.resize(rect.width, rect.height, dpr);

        const b = dom.bbox;
        const corners: [number, number][] = (
          [
            [b[0], b[1]],
            [b[2], b[1]],
            [b[2], b[3]],
            [b[0], b[3]],
          ] as LngLatLike[]
        ).map((ll) => {
          const p = map.project(ll);
          return [p.x * dpr, p.y * dpr] as [number, number];
        });
        renderer.setMatrix(unitSquareToQuad(corners));
        renderer.render(dt);
      }
      rafRef.current = requestAnimationFrame(frame);
    };
    rafRef.current = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(rafRef.current);
      renderer.dispose();
      rendererRef.current = null;
    };
  }, []);

  // --- push the solved field into the renderer -----------------------------
  useEffect(() => {
    const r = rendererRef.current;
    if (r && slice) r.setField(slice);
  }, [slice, domain]);

  // --- push the wind-independent terrain fields ----------------------------
  useEffect(() => {
    const r = rendererRef.current;
    if (r && terrainLayers) r.setTerrainLayers(terrainLayers);
  }, [terrainLayers, domain]);

  useEffect(() => {
    rendererRef.current?.setOptions({
      particles: layers.particles,
      raster: layers.raster,
      rasterOpacity: layers.rasterOpacity,
      particleOpacity: layers.particleOpacity,
      turbulence: layers.turbulence,
      // Animation rate follows the free-stream wind, so a light day visibly
      // drifts and a strong day visibly rips.
      crossTimeSeconds: crossTimeForWind(windSpeedMs),
      // The speed-up raster divides by this, so it must be the wind input
      // rather than anything derived from the field.
      freeStreamSpeed: windSpeedMs,
      watermarkAltitude: layers.watermarkAltitude,
      hikeFlyWind: layers.hikeFlyWind,
      windDir: metDegreesToVector(windDirectionDeg),
    });
  }, [layers, windSpeedMs, windDirectionDeg]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer('hillshade')) return;
    map.setLayoutProperty('hillshade', 'visibility', layers.hillshade ? 'visible' : 'none');
  }, [layers.hillshade]);

  // --- vector layers -------------------------------------------------------
  //
  // The desired GeoJSON is kept in a ref and pushed by `syncVectorSources`,
  // which runs both when the inputs change and whenever the style (re)loads.
  // Writing straight from an effect and bailing on `!isStyleLoaded()` looks
  // equivalent but silently drops data: if the style is still loading when the
  // effect fires, nothing retries until a dependency happens to change again —
  // which, for a layer toggle that is already in its final state, may be never.
  const syncVectorSources = useCallback(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    const desired = vectorDataRef.current;
    if (!desired) return;
    for (const [id, data] of Object.entries(desired)) {
      const src = map.getSource(id) as maplibregl.GeoJSONSource | undefined;
      src?.setData(data);
    }
  }, []);

  useEffect(() => {
    if (!domain) {
      vectorDataRef.current = null;
      return;
    }
    const toLngLat = (p: [number, number]): [number, number] =>
      gridToLonLat(domain, p[0], p[1]);
    const b = domain.bbox;

    vectorDataRef.current = {
      separation:
        contours && layers.separation
          ? {
              type: 'FeatureCollection',
              features: contours.separation.map((rings) => ({
                type: 'Feature' as const,
                properties: {},
                geometry: {
                  type: 'Polygon' as const,
                  coordinates: rings.map((ring) => ring.map(toLngLat)),
                },
              })),
            }
          : emptyFc(),
      lift:
        contours && layers.liftBand
          ? {
              type: 'FeatureCollection',
              features: contours.lift.map((line) => ({
                type: 'Feature' as const,
                properties: {},
                geometry: {
                  type: 'LineString' as const,
                  coordinates: line.map(toLngLat),
                },
              })),
            }
          : emptyFc(),
      domain: {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: [
                [b[0], b[1]],
                [b[2], b[1]],
                [b[2], b[3]],
                [b[0], b[3]],
                [b[0], b[1]],
              ],
            },
          },
        ],
      },
    };
    syncVectorSources();
  }, [contours, domain, layers.separation, layers.liftBand, syncVectorSources]);

  // Re-push whenever the style finishes loading or is replaced.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.on('styledata', syncVectorSources);
    map.on('load', syncVectorSources);
    return () => {
      map.off('styledata', syncVectorSources);
      map.off('load', syncVectorSources);
    };
  }, [syncVectorSources]);

  // --- cross-section drawing ----------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    let first: [number, number] | null = null;
    const canvas = map.getCanvas();

    const onClick = (e: maplibregl.MapMouseEvent) => {
      if (!useStore.getState().crossSectionMode) return;
      const pt: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      if (!first) {
        first = pt;
        setSectionPreview(map, [pt, pt]);
      } else {
        setState({ crossSectionLine: [first, pt], crossSectionMode: false });
        setSectionPreview(map, [first, pt]);
        first = null;
      }
    };
    const onMove = (e: maplibregl.MapMouseEvent) => {
      if (!first || !useStore.getState().crossSectionMode) return;
      setSectionPreview(map, [first, [e.lngLat.lng, e.lngLat.lat]]);
    };

    map.on('click', onClick);
    map.on('mousemove', onMove);
    canvas.style.cursor = crossSectionMode ? 'crosshair' : '';

    return () => {
      map.off('click', onClick);
      map.off('mousemove', onMove);
      canvas.style.cursor = '';
    };
  }, [crossSectionMode, setState]);

  return (
    <div className="map-root">
      <div ref={containerRef} className="map-container" />
      <canvas ref={canvasRef} className="flow-overlay" />
    </div>
  );
}

function emptyFc(): GeoJSON.FeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}

function setSectionPreview(map: MlMap, line: [[number, number], [number, number]]) {
  const src = map.getSource('section') as maplibregl.GeoJSONSource | undefined;
  src?.setData({
    type: 'FeatureCollection',
    features: [
      { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: line } },
    ],
  });
}

